import React, { useRef, useState } from "react";

import { SearchOutlined } from "@ant-design/icons";
import { Input, MenuProps } from "antd";
import { AnimatePresence, motion } from "motion/react";

import { PendoId } from "../../utils/pendo";
import { CollapseTransition, TRANSITION_DURATION } from "../CollapsibleHeight";
import { Divider } from "../Divider";
import { LinkButton } from "../LinkButton";
import { Section } from "../Section";

import { AddFilterButton } from "./AddFilterButton";
import * as theme from "./FilterBar.css";
import { FilterBarHeaderActions } from "./FilterBarHeaderActions";

const DEBOUNCE_TIME = 350;

export interface FilterBarProps {
    pendoIds?: { toggle: PendoId; add: PendoId; clearAll: PendoId };
    children: React.ReactNode;
    addLabel: string;
    hideFiltersLabel: string;
    clearAllLabel: string;
    getFilterCountLabel: (filterCount: number) => string;
    addFilterItems: MenuProps["items"];
    filterCount: number;

    /** Omit to hide the search input. */
    globalSearch?: {
        label: string;
        onChange: (value: string) => void;
        getValue?: () => string | undefined;
    };
    rowCountLabel: React.ReactNode | undefined;
    onClearFilters: () => void;
    /** Controlled collapsed state. Pair with onCollapsedChange; omit for uncontrolled. */
    collapsed?: boolean;
    onCollapsedChange?: (collapsed: boolean) => void;
    clearable?: boolean;
    /**
     * Always-visible filters rendered above the collapsible filter rows.
     * Not counted automatically — report active ones via `extraFilterCount`.
     * Unaffected by "clear all".
     */
    aboveFilters?: React.ReactNode;
    /**
     * Always-visible filters rendered below the filter rows.
     * Not counted automatically — report active ones via `extraFilterCount`.
     * Unaffected by "clear all".
     */
    belowFilters?: React.ReactNode;
    extraFilterCount?: number;
    headerActions?: React.ReactNode;
}
export function FilterBar({
    children,
    addLabel,
    globalSearch,
    getFilterCountLabel,
    hideFiltersLabel,
    clearAllLabel,
    filterCount,
    addFilterItems,
    rowCountLabel,
    onClearFilters,
    clearable,
    collapsed,
    onCollapsedChange,
    aboveFilters,
    belowFilters,
    extraFilterCount,
    pendoIds,
    headerActions,
}: FilterBarProps) {
    const [internalHidden, setInternalHidden] = useState(false);

    const isControlled = collapsed !== undefined && !!onCollapsedChange;
    const isHidden = isControlled ? collapsed : internalHidden;
    const setIsHidden = isControlled ? onCollapsedChange : setInternalHidden;

    const debounceTimeout = useRef<ReturnType<typeof setTimeout>>(undefined);

    const handleSearch = (value: string) => {
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        debounceTimeout.current = setTimeout(() => {
            globalSearch?.onChange(value);
        }, DEBOUNCE_TIME);
    };

    const defaultSearchValue = globalSearch?.getValue?.();

    const hasAddFilterItems = (addFilterItems?.length ?? 0) > 0;

    // Count shown in the pill / count label.
    const totalFilterCount = filterCount + (extraFilterCount ?? 0);

    // Custom (above/below) filters are always visible, so their presence — not
    // their count — is what puts the bar into "active filters" mode.
    const hasCustomFilters = extraFilterCount !== undefined;

    // "Active filters" mode shows the collapse toggle + footer.
    const hasFilters = totalFilterCount > 0 || hasCustomFilters;

    // Custom filters can't be cleared from here yet, so "clear all" / cancel
    // act on removable (column) filters only.
    const hasRemovableFilters = filterCount > 0 || (extraFilterCount ?? 0) > 0;
    const isClearable = (clearable ?? true) && hasRemovableFilters;

    const handleClearFilters = () => {
        setIsHidden(false);
        onClearFilters();
    };
    return (
        <div className={theme.root}>
            <Section>
                <div className={theme.content}>
                    <div className={theme.topRow}>
                        <FilterBarHeaderActions
                            pendoIds={pendoIds}
                            isHidden={isHidden}
                            setIsHidden={setIsHidden}
                            addLabel={addLabel}
                            addFilterItems={addFilterItems}
                            hideFiltersLabel={hideFiltersLabel}
                            getFilterCountLabel={getFilterCountLabel}
                            filterCount={totalFilterCount}
                            rowCountLabel={rowCountLabel}
                            onClearFilters={handleClearFilters}
                            clearable={isClearable}
                            hasFilters={hasFilters}
                        />
                        {globalSearch && (
                            <div className={theme.addonAfter}>
                                <Input
                                    onChange={(e) =>
                                        handleSearch(e.target.value)
                                    }
                                    placeholder={globalSearch.label}
                                    addonAfter={<SearchOutlined />}
                                    name="table-global-search"
                                    allowClear
                                    defaultValue={defaultSearchValue}
                                />
                            </div>
                        )}
                        {headerActions}
                    </div>
                    <CollapseTransition collapsed={isHidden}>
                        <div>
                            <AnimatePresence initial={false}>
                                {aboveFilters}
                            </AnimatePresence>
                            <AnimatePresence initial={false}>
                                {children}
                            </AnimatePresence>
                            <AnimatePresence initial={false}>
                                {belowFilters}
                            </AnimatePresence>
                            <AnimatePresence initial={false}>
                                {hasFilters && (
                                    <motion.div
                                        initial={{ height: 0 }}
                                        animate={{ height: "auto" }}
                                        exit={{ height: 0 }}
                                        transition={{
                                            duration: TRANSITION_DURATION,
                                        }}
                                        className={theme.filtersFooter}
                                    >
                                        <Divider />
                                        <div className={theme.filtersButtons}>
                                            {hasAddFilterItems && (
                                                <AddFilterButton
                                                    data-pendo={pendoIds?.add}
                                                    addLabel={addLabel}
                                                    items={addFilterItems}
                                                />
                                            )}
                                            {isClearable && (
                                                <LinkButton
                                                    data-pendo={
                                                        pendoIds?.clearAll
                                                    }
                                                    onClick={handleClearFilters}
                                                >
                                                    {clearAllLabel}
                                                </LinkButton>
                                            )}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </CollapseTransition>
                </div>
            </Section>
        </div>
    );
}
